/**
 * PublicLanguageSwitcher — a compact, DA-faithful language selector for the
 * PUBLIC / logged-out surfaces.
 *
 * The authenticated shell already lets an operator switch language through the
 * avatar menu (`LanguageMenu`), but a logged-out visitor had no way to do it.
 * CH-3: this component was written for that gap and then mounted in ONE place
 * (`AuthShell`, i.e. login / register), so the landing, /pricing, /legal and
 * every other marketing page still left the visitor stuck with whatever the
 * browser detector picked. It is now mounted, unforked, on:
 *   - the landing header, left of the « Enter » CTA (desktop); below `sm` that
 *     desktop cluster is hidden and the entry moves into the mobile menu panel,
 *     whose rows are `min-h-14`. This trigger's own floor is 44px
 *     (`min-h-11 min-w-11`) — an earlier version of this comment justified the
 *     split by "a third 48px target", a size this control has never had;
 *   - the landing's legal bar (`ConvertFooter`) and the bottom bar of
 *     `components/layout/Footer.tsx`, which covers every public content page
 *     that mounts that footer. The authoritative list is the enumeration in
 *     `Footer.tsx`'s bottom-bar comment, checked against the route table in
 *     `App.tsx`. Do NOT reach for `grep -rl '<Footer' src/`: it answers 18 for
 *     13 routes, because it also counts `LegalDocument`, two test files,
 *     `<FooterLockup` in ExactLanding, and three files — `Footer.tsx`,
 *     `legalLinks.ts` and this one — where the string only ever appears inside
 *     a comment about the grep.
 *
 * It stays ONE component across those grounds: what changes between a cream
 * auth screen, the halo header and an ink footer is a `variant`, not a fork.
 *
 * The DA is the aikka square one: `os.css` tokens only, 5px radii, no
 * pill/circle radii (see the `check:os-da` gate) — a globe trigger that opens a
 * small menu of the SUPPORTED languages. That list is fr / en / es today;
 * `de` and `it` are deliberately dormant (see `i18n/languages.ts`), and reading
 * `SUPPORTED_LANGUAGES` rather than a local list is what keeps them out.
 *
 * Persistence and `<html lang>` are wired ONCE, centrally, in `src/i18n/index.ts`
 * (the browser LanguageDetector caches the choice in localStorage under
 * `aikka-lang`; a `languageChanged` listener syncs the html attribute). This
 * component therefore does one thing on select — call `i18n.changeLanguage` —
 * and persistence + app-wide re-render follow, so there is a single source of
 * truth. It reuses the shared `SUPPORTED_LANGUAGES` list and the already-
 * translated `settings:language.*` labels (no new copy to translate).
 *
 * Accessibility mirrors the account menu: `role="menu"` with
 * `role="menuitemradio"` items, Esc to close (returns focus to the trigger),
 * ↑/↓/Home/End roving focus and close-on-outside-click.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Globe } from 'lucide-react';
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from '@/i18n/languages';
import { applyLanguage } from '@/i18n/applyLanguage';

/**
 * The ground the switcher is standing on — NOT a theme, a context.
 *
 * `surface`  — an OS control on a light surface: bordered chip, `bg-surface-2`,
 *              ink glyphs. What the auth screens and `Footer.tsx` sit on.
 * `on-light` — the landing header over the hero halo, or over its cream scrolled
 *              plate. Ground-less on purpose: the CEO had the plate removed from
 *              behind the header lockup, so this reads exactly like the
 *              Method / Modules links next to it (same `#2f2c28`, same hover
 *              wash) and takes its legibility from the `.nav-link` halo the
 *              caller adds through `className`.
 * `on-ink`   — over a section that declares itself `data-chrome="dark"`: the
 *              header past the hero, the mobile menu panel, the ConvertFooter.
 *              Glyphs invert to white; `text-foreground` on #020202 is the very
 *              unreadability CH-1 was about.
 *
 * The dropdown itself does not vary: it is paper on every ground, which reads
 * against all three, and one popup is one thing to keep accessible.
 */
export type PublicLanguageSwitcherVariant = 'surface' | 'on-light' | 'on-ink' | 'ink-solid';

/**
 * KEYBOARD FOCUS IS A RING, ON ALL THREE GROUNDS — not a border tint.
 *
 * The trigger sets `focus-visible:outline-none`, so whatever replaces it IS the
 * focus indicator — and WCAG 2.2 asks a non-text indicator for 3:1 against what
 * it sits on. A border swap did not get there: `on-light` answered focus with
 * `border-black/25` — 25% black over #EFEEEA composites to ~#B3B2AF, under 2:1
 * against the cream it sits on. That made the one new control on the landing
 * header strictly harder to keyboard than the Method / Modules links beside it,
 * which never dropped their UA outline.
 * Each variant now carries a 2px ring drawn in its OWN foreground colour:
 * `#2f2c28` on cream, the `foreground` token on the OS surface, white on
 * #020202. Each of those is a text/ground pair the design system already holds
 * to 4.5:1, so the ring clears the 3:1 floor by construction rather than by a
 * number written in a comment. `ring-2` carries no offset, so it hugs the
 * border box and paints nothing BEHIND the trigger: the ground-less variants
 * stay ground-less.
 */
const TRIGGER_VARIANT: Record<PublicLanguageSwitcherVariant, string> = {
  /**
   * `ink-solid` — le déclencheur des écrans d'authentification.
   *
   * Le client a demandé « bouton noir » (Notion → Onboarding → Lang). Il était
   * en `surface` : un filet gris sur un fond gris, posé en haut à droite d'une
   * page crème quasi vide, où il se lisait comme un artefact plutôt que comme
   * un contrôle. En encre pleine il devient le seul objet dense de la page
   * avant le formulaire — ce qu'il est : la première décision qu'on peut
   * prendre sur cet écran, avant même de taper son e-mail.
   *
   * Le halo de focus passe au crème pour rester visible SUR l'encre.
   */
  'ink-solid':
    'border border-ink bg-ink text-white hover:opacity-90 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ink',
  surface:
    'border border-border bg-surface-2 text-foreground hover:border-signal/50 hover:text-foreground focus-visible:border-state/60 focus-visible:ring-2 focus-visible:ring-foreground',
  'on-light':
    'border border-transparent bg-transparent text-[#2f2c28] hover:bg-black/[0.06] hover:text-ink focus-visible:border-black/25 focus-visible:ring-2 focus-visible:ring-[#2f2c28]',
  'on-ink':
    'border border-transparent bg-transparent text-white hover:bg-white/10 focus-visible:border-white/40 focus-visible:ring-2 focus-visible:ring-white',
};

/**
 * Le jour d'air laissé entre le menu déroulant et le bord de l'écran quand il
 * faut le recadrer. 8px : la moitié de la gouttière de page la plus étroite du
 * site (`px-4` sur le header à 320), assez pour que le menu ne colle pas au
 * bord sans avoir l'air décroché de son déclencheur.
 */
const VIEWPORT_MARGIN_PX = 8;

/**
 * LE FOND DU MENU SUIT CELUI DE SON DÉCLENCHEUR.
 *
 * Il était `bg-paper` pour les quatre variantes : une fenêtre claire sous un
 * bouton d'encre, une fenêtre claire sous un bouton transparent posé sur du
 * noir. Le client a demandé « le fond de la même couleur que le bouton qui
 * déclenche le sub menu ».
 *
 * Deux familles, parce qu'il n'y a que deux sols possibles :
 *   · ENCRE — `ink-solid` (le bouton noir des écrans d'auth) et `on-ink` (le
 *     pied de page de la home, sur le sunburst). Le menu est encre, le texte
 *     crème.
 *   · CLAIR — `surface` (le contrôle OS) et `on-light` (l'en-tête de la home).
 *     Le déclencheur y est transparent ou en `surface-2` ; le menu prend le
 *     `surface-2`, qui EST le sol de contrôle de ces deux cas.
 *
 * Un menu transparent serait illisible : le fond suit la COULEUR du bouton, pas
 * son alpha.
 */
const MENU_VARIANT: Record<PublicLanguageSwitcherVariant, string> = {
  'ink-solid': 'border-ink bg-ink',
  'on-ink': 'border-white/20 bg-ink',
  surface: 'border-border bg-surface-2',
  'on-light': 'border-black/15 bg-surface-2',
};

/** La rangée du menu, par famille de sol. */
const MENU_ITEM_VARIANT: Record<PublicLanguageSwitcherVariant, { idle: string; active: string }> = {
  'ink-solid': { idle: 'text-white/70 hover:bg-white/10 hover:text-white', active: 'bg-white/15 text-white' },
  'on-ink': { idle: 'text-white/70 hover:bg-white/10 hover:text-white', active: 'bg-white/15 text-white' },
  surface: { idle: 'text-ink-2 hover:bg-secondary hover:text-foreground', active: 'bg-secondary text-foreground' },
  'on-light': { idle: 'text-ink-2 hover:bg-secondary hover:text-foreground', active: 'bg-secondary text-foreground' },
};

/**
 * LES RANGÉES QUAND LE MENU N'A PAS DE SOL À LUI (`menuPlate`).
 *
 * `MENU_ITEM_VARIANT` marque l'actif par un FOND — `bg-secondary` clair,
 * `bg-white/15` sur l'encre. Posé dans une fenêtre sans fond ni bordure, ce
 * pavé redevient exactement la boîte qu'on venait d'enlever : le menu lisait
 * « une plaque flottante » alors que la consigne est « totalement fondu ».
 *
 * L'état actif passe donc au TYPE : encre pleine et graisse contre gris et
 * graisse normale. `aria-checked` porte déjà l'information pour un lecteur
 * d'écran (posé sur la rangée), donc rien ne se perd à la retirer de l'œil.
 * Le survol garde un lavis très léger — c'est un retour de pointeur, pas un
 * état, et sans lui la rangée ne répond plus du tout.
 */
const MENU_ITEM_BLENDED: Record<PublicLanguageSwitcherVariant, { idle: string; active: string }> = {
  'ink-solid': { idle: 'text-white/60 hover:bg-white/10 hover:text-white', active: 'font-medium text-white' },
  'on-ink': { idle: 'text-white/60 hover:bg-white/10 hover:text-white', active: 'font-medium text-white' },
  surface: { idle: 'text-ink-2 hover:bg-black/[0.06] hover:text-foreground', active: 'font-medium text-foreground' },
  'on-light': { idle: 'text-ink-2 hover:bg-black/[0.06] hover:text-foreground', active: 'font-medium text-foreground' },
};

/** Le halo de focus, lisible sur chacun des deux sols. */
const MENU_RING_VARIANT: Record<PublicLanguageSwitcherVariant, string> = {
  'ink-solid': 'focus-visible:ring-white',
  'on-ink': 'focus-visible:ring-white',
  surface: 'focus-visible:ring-foreground',
  'on-light': 'focus-visible:ring-foreground',
};

/** On the ground-less variants the globe and chevron ride the label's colour. */
const GLYPH_VARIANT: Record<PublicLanguageSwitcherVariant, string> = {
  'ink-solid': 'text-current',
  surface: 'text-ink-2',
  'on-light': 'text-current',
  'on-ink': 'text-current',
};

interface PublicLanguageSwitcherProps {
  /** Extra classes for the trigger (positioning from the caller). */
  className?: string;
  /** Which edge the menu aligns to. Defaults to the right (header/top-right use). */
  align?: 'start' | 'end';
  /** Ground the trigger is drawn against. Defaults to the OS surface control. */
  variant?: PublicLanguageSwitcherVariant;
  /**
   * Which way the menu opens. `top` for a footer: a menu dropping DOWN from the
   * last bar of the page opens past the end of the document, where a visitor
   * has to scroll to reach items that were supposed to be one click away.
   */
  side?: 'bottom' | 'top';
  /**
   * LE SOL DU MENU, IMPOSÉ PAR L'APPELANT.
   *
   * Le client : « il faut que ce soit la même couleur que la navbar au même
   * moment, pas de bordure, je veux que ce soit totalement fondu ». Or la barre
   * publique change de sol en défilant — rien au-dessus du halo, crème à 85 %
   * au-dessus d'une section claire, noir à 70 % au-dessus d'une section
   * d'encre — et une `variant` ne connaît que la FAMILLE du sol, pas l'état du
   * scroll. C'est donc l'appelant, qui lui le connaît, qui passe la plaque.
   *
   * Quand elle est donnée elle REMPLACE le fond et la bordure de la variante,
   * et l'ombre portée tombe avec : une ombre est ce qui décolle une fenêtre de
   * son fond, exactement ce qu'on ne veut plus. La `variant` continue de porter
   * la couleur du TYPE, qui doit rester lisible sur la plaque choisie.
   */
  menuPlate?: string;
}

export function PublicLanguageSwitcher({
  className = '',
  align = 'end',
  variant = 'surface',
  side = 'bottom',
  menuPlate,
}: PublicLanguageSwitcherProps) {
  const { t, i18n } = useTranslation('settings');
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const current =
    SUPPORTED_LANGUAGES.find((l) => i18n.resolvedLanguage === l.code) ??
    SUPPORTED_LANGUAGES.find((l) => i18n.language?.startsWith(l.code)) ??
    SUPPORTED_LANGUAGES.find((l) => l.code === DEFAULT_LANGUAGE) ??
    SUPPORTED_LANGUAGES[0]!;

  const close = useCallback((returnFocus = true) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  const pick = useCallback(
    (code: string) => {
      /*
       * `applyLanguage` et non `i18n.changeLanguage` : un choix explicite doit
       * aussi retirer de l'URL le `?lang=` qui reprendrait le dessus au
       * prochain chargement complet (`i18n/applyLanguage.ts`).
       */
      applyLanguage(code);
      close();
    },
    [close],
  );

  /**
   * RECADRAGE DANS LE VIEWPORT — déduit de la géométrie, pas d'un rapport.
   *
   * D'ABORD LA SOURCE, parce qu'une version antérieure de ce commentaire
   * annonçait « mesuré à 320px sur /pricing (audit responsive) … de -18px à
   * 158 » : `audit:responsive` NE PEUT PAS produire ce chiffre.
   * `scripts/responsive-audit/run.mjs` n'ouvre que les deux menus déclarés
   * dans `MOBILE_MENUS` — le panneau plein écran de la landing et la nav
   * dépliée des pages marketing — et n'a jamais cliqué CE déclencheur-ci.
   * Aucun rapport commité ne contient donc de constat sur ce menu.
   *
   * Ce qui reste vrai, et se relit sur les classes : le menu est `absolute`,
   * aligné sur un bord de son déclencheur (`right-0` par défaut), et large
   * d'au moins 176px (`min-w-[11rem]`). Rien, dans ce placement, ne connaît le
   * viewport. Dès que le bord droit du déclencheur tombe à moins de 176px du
   * bord gauche de l'écran — ce qui arrive aux petits viewports, où le
   * déclencheur est le premier élément d'une rangée — le menu part en x
   * négatif : un déclencheur dont le bord droit est à 158px poserait le menu
   * de -18 à 158, et ces dix-huit pixels seraient rognés par le
   * `overflow-x: hidden` du body, donc inatteignables. C'est le défaut que la
   * carte demande de vérifier « quand le déclencheur est près d'un bord » ; le
   * test unitaire de ce composant le rejoue avec des boîtes fabriquées, faute
   * d'un instrument qui ouvre ce menu.
   *
   * Pourquoi une MESURE et pas une media query, alors que ce lot préfère le CSS
   * partout où c'est possible : le débordement ne dépend pas de la largeur du
   * viewport mais de la position du déclencheur DANS ce viewport, que le CSS ne
   * peut pas lire. Et le piège du prerender ne s'applique pas ici : ce menu
   * n'existe dans le DOM qu'après un clic, jamais dans le HTML statique — il
   * n'y a pas de branche à figer, rien qu'une correction appliquée à un élément
   * que le serveur n'a jamais rendu.
   *
   * `useLayoutEffect` : la correction est posée AVANT la peinture, sinon le
   * menu s'affiche une frame à sa position fautive. La mesure part de la boîte
   * NATURELLE (`rect - décalage courant`), donc réappliquer l'effet converge au
   * lieu de dériver. Une boîte de largeur nulle — jsdom, mise en page absente —
   * ne conclut rien : pas de mesure, pas de correction.
   */
  const [shiftX, setShiftX] = useState(0);
  useLayoutEffect(() => {
    if (!open) return undefined;
    const el = menuRef.current;
    if (!el) return undefined;
    const fit = () => {
      const rect = el.getBoundingClientRect();
      if (rect.width === 0) return;
      const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
      setShiftX((previous) => {
        const left = rect.left - previous;
        const right = rect.right - previous;
        if (left < VIEWPORT_MARGIN_PX) return Math.round(VIEWPORT_MARGIN_PX - left);
        if (right > viewportWidth - VIEWPORT_MARGIN_PX) {
          return Math.round(viewportWidth - VIEWPORT_MARGIN_PX - right);
        }
        return 0;
      });
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [open]);

  // Move keyboard focus into the menu when it opens (parity with the avatar menu).
  useEffect(() => {
    if (!open) return;
    const items = menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitemradio"]');
    const activeIdx = SUPPORTED_LANGUAGES.findIndex((l) => l.code === current.code);
    items?.[activeIdx >= 0 ? activeIdx : 0]?.focus();
  }, [open, current.code]);

  // Close on any click outside the trigger + menu subtree.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) close(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open, close]);

  const onMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const menu = menuRef.current;
    if (!menu) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    const items = Array.from(menu.querySelectorAll<HTMLElement>('[role="menuitemradio"]'));
    if (items.length === 0) return;
    const currentIdx = items.indexOf(document.activeElement as HTMLElement);
    const focusAt = (idx: number) => {
      const next = ((idx % items.length) + items.length) % items.length;
      items[next]?.focus();
    };
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        focusAt(currentIdx + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        focusAt(currentIdx - 1);
        break;
      case 'Home':
        e.preventDefault();
        focusAt(0);
        break;
      case 'End':
        e.preventDefault();
        focusAt(items.length - 1);
        break;
      case 'Tab':
        e.preventDefault();
        focusAt(currentIdx + (e.shiftKey ? -1 : 1));
        break;
      default:
        break;
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        data-testid="public-language-switcher"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('language.select')}
        onClick={() => (open ? close(false) : setOpen(true))}
        data-variant={variant}
        // 44×44 MINIMUM, everywhere this trigger is mounted. `px-2.5 py-1.5`
        // around a 16px globe measured ~30px high, and it shipped as-is into the
        // landing's mobile panel (whose other rows are `min-h-14`) and into the
        // footer bars — a pointer target well under the 44px floor, on the one
        // control a visitor on a phone needs to read the site at all. The width
        // is padded too: `min-w-11` matters the day the trigger is rendered
        // icon-only.
        className={`inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center gap-1.5 rounded-control px-2.5 py-1.5 text-[0.8rem] font-medium transition-colors focus-visible:outline-none ${TRIGGER_VARIANT[variant]} ${className}`}
      >
        <Globe className={`h-4 w-4 ${GLYPH_VARIANT[variant]}`} strokeWidth={1.7} aria-hidden />
        <span className="font-data text-[0.72rem] uppercase tracking-wider">{current.short}</span>
        <ChevronDown
          className={`h-3.5 w-3.5 ${GLYPH_VARIANT[variant]} transition-transform ${open ? 'rotate-180' : ''}`}
          strokeWidth={1.8}
          aria-hidden
        />
      </button>

      {open && (
        <div
          ref={menuRef}
          role="menu"
          aria-label={t('language.select')}
          data-testid="public-language-menu"
          onKeyDown={onMenuKeyDown}
          // `max-w-` borne la BOÎTE au viewport (un menu plus large que
          // l'écran ne se recadre pas, il se coupe des deux côtés) ; le
          // `translateX` est la correction mesurée ci-dessus, en transform pour
          // ne pas rentrer dans la mise en page du bord auquel le menu est
          // aligné.
          style={shiftX === 0 ? undefined : { transform: `translateX(${shiftX}px)` }}
          /* LARGEUR DU DÉCLENCHEUR, ET COLLÉ DESSOUS.
             Il ouvrait à `min-w-[11rem]` (176px) sous un bouton d'environ 80,
             avec 6px d'air : une fenêtre deux fois plus large que ce qui
             l'ouvre, flottant à côté. Le client : « je veux que le submenu soit
             de la même taille … et pas plus grand, juste vraiment en dessous ».
             `w-full` mesure le parent `relative`, qui n'entoure que le bouton —
             c'est donc EXACTEMENT sa largeur, sans rien recopier. Le jour où le
             déclencheur change de rembourrage, le menu suit.
             `top-full` / `bottom-full` : aucun interstice. */
          className={`absolute z-50 w-full max-w-[calc(100vw-1rem)] overflow-hidden rounded-control py-1 ${
            menuPlate
              ? menuPlate
              : `border shadow-[0_18px_50px_-28px_hsl(0_0%_6.67%/0.55)] ${MENU_VARIANT[variant]}`
          } ${side === 'top' ? 'bottom-full' : 'top-full'} ${
            align === 'end' ? 'right-0' : 'left-0'
          }`}
        >
          {SUPPORTED_LANGUAGES.map((lang) => {
            const active = lang.code === current.code;
            return (
              <button
                key={lang.code}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                data-testid={`public-language-${lang.code}`}
                onClick={() => pick(lang.code)}
                // Same 44px floor as the trigger: `py-2` around a 0.83rem line
                // is ~36px, and these are the targets the visitor actually aims
                // at once the menu is open.
                //
                // KEYBOARD FOCUS IS A RING HERE TOO, ON EVERY ITEM.
                // The previous class list killed the UA outline with a bare
                // `outline-none` (every state, not just focus-visible) and gave
                // the replacement to the INACTIVE branch only, as a
                // `focus-visible:bg-secondary` wash. The ACTIVE item had none —
                // and the effect above deliberately opens the menu with focus
                // ON the active item, so the FIRST element a keyboard user
                // reached here was the one with no indicator at all
                // (WCAG 2.4.7). The ring now lives in the shared part of the
                // class list, where a branch cannot drop it again.
                // `ring-inset`, because the menu container is `overflow-hidden`
                // and an outset ring would be clipped on the first and last
                // rows. La COULEUR du halo suit désormais le sol du menu
                // (`MENU_RING_VARIANT`) : encre sous un menu clair, crème sous
                // un menu d'encre. Chacune des deux paires est déjà tenue à
                // 4,5:1 pour du texte par le système, donc le plancher de 3:1
                // d'un indicateur non textuel est franchi par construction —
                // même raisonnement que TRIGGER_VARIANT plus haut, pas un
                // chiffre inventé ici.
                className={`flex min-h-11 w-full items-center justify-center rounded-control px-2 py-2 font-data text-[0.72rem] uppercase tracking-wider transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset ${MENU_RING_VARIANT[variant]} ${
                  (menuPlate ? MENU_ITEM_BLENDED : MENU_ITEM_VARIANT)[variant][
                    active ? 'active' : 'idle'
                  ]
                }`}
              >
                {/* L'ABRÉVIATION SEULE — « avec FR, EN, etc. ».
                    La rangée portait l'abréviation PUIS le nom natif PUIS une
                    coche : trois colonnes, qui sont ce qui forçait la fenêtre à
                    176px. À la largeur du déclencheur il n'y a de place que
                    pour ce qui distingue, et c'est le code.
                    L'état actif se lit au fond et à l'encre pleine, pas à une
                    coche : dans 80px, une icône reprendrait le quart de la
                    rangée. `aria-checked` porte déjà l'information pour un
                    lecteur d'écran, et il est posé plus haut.
                    Le nom natif n'est pas perdu pour autant : c'est le
                    `aria-label` du déclencheur qui nomme le contrôle, et le
                    menu de l'application (`LanguageMenu`) le rend en toutes
                    lettres, avec la place pour le faire. */}
                {lang.short}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default PublicLanguageSwitcher;
